/* eslint-disable sonarjs/no-duplicate-string */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ResponseGeneric } from 'mbs-ui-shared@components/components/models/generic.model';
import {
  AssignRequest,
  AutoRenewalRequest,
  Balance,
  Billing,
  Debt,
  DebtInvoicesResponse,
  FreeLicense,
  GetAvailablePaidLicensesParams,
  GroupByCompanyParams,
  GroupByCompanyResponse,
  LicensePool,
  LicensePoolLimit,
  LicenseStatistic,
  LicenseTypeDto,
  LicenseUsageItem,
  ManageRequest,
  NextPaymentData,
  PayDebt,
  PostPaymentParams,
  PrePaymentAutoRenewPayload,
  PrePaymentGroup,
  PrePaymentLicense,
  PrePaymentParams,
  PrePaymentPayload,
  PrePaymentResponse,
  PrePaymentResponseGrouped,
  PrePaymentTotals,
  ReleaseRequest,
  RenewResponse,
  SelectLicenseParams,
  Total,
  UsageHistoryGroupedResponse,
  UsageHistoryResponse
} from 'mbs-ui-shared@components/components/models/license.model';
import {
  AdministratorLicense,
  AvailableLicenseType,
  AvailableLicenses,
  AvailablePaidLicense,
  ComputerGrantLicenseResultDTO,
  ComputerLicense,
  ExtendedComputerLicense,
  LicenseKind,
  LicenseTypeSummary
} from 'mbs-ui-shared@models/License';
import { AdministratorLicenseSummaryInfo } from 'mbs-ui-shared@models/licenses/administrator-license-summary-info';
import { Observable } from 'rxjs';

import {
  GetPurchasePayload,
  GetTotalPayload,
  LicensesPricesData,
  TotalResponse
} from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { MANUAL_ERROR_HANDLED } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { LicenseType } from 'mbs-ui-shared@models';
import { PermissionFlags } from 'mbs-ui-shared@models/PermissionFlags';
import { objectToParams } from 'mbs-ui-shared@utils/object-to-params';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root'
})
export class LicensesService {
  private http = inject(HttpClient);
  private config = inject(ConfigurationService);

  private readonly baseHref = '/api/licenses';
  private readonly baseProviderHref = '/api/licenses/provider';
  private readonly baseVersionedSuffix = '/api/v1/licenses';
  private readonly baseComputersHref = '/api/computers';
  private readonly baseAdministratorsHref = '/api/administrators';
  private readonly baseSubscriptionHref = 'api/subscription';
  private readonly baseVersionedLimitsSuffix = '/api/v1/Limits';
  private readonly licensesServiceUrl = '';
  private readonly baseVersionedHref: string;
  private readonly baseVersionedLimitsHref: string;

  constructor() {
    const config = this.config;

    this.licensesServiceUrl = config.get('licensesHref');
    this.baseVersionedHref = this.licensesServiceUrl + this.baseVersionedSuffix;
    this.baseVersionedLimitsHref = this.licensesServiceUrl + this.baseVersionedLimitsSuffix;
  }

  public getBackupComputerLicenses(hid: string): Observable<Array<ExtendedComputerLicense>> {
    const availableLicenseKinds = [LicenseKind.Backup, LicenseKind.VmSocket];

    return this.getComputerLicenses(hid, availableLicenseKinds);
  }

  public getRMMComputerLicenses(hid: string): Observable<Array<ExtendedComputerLicense>> {
    const availableLicenseKinds = [LicenseKind.Rmm, LicenseKind.Connect];

    return this.getComputerLicenses(hid, availableLicenseKinds);
  }

  private getComputerLicenses(hid: string, availableLicenseKinds: LicenseKind[]): Observable<Array<ExtendedComputerLicense>> {
    return this.http.get<Array<ExtendedComputerLicense>>(`${this.baseHref}/computer`, { params: { hid, kind: availableLicenseKinds } });
  }

  public getAvailableLicensesByCompanyId(
    hid: string,
    licenseTypeId: LicenseType,
    companyId: string,
    userId: string
  ): Observable<AvailableLicenses> {
    return this.http.get<AvailableLicenses>(`${this.baseHref}/authorize/availablelicenses`, {
      params: {
        hid,
        licenseTypeId,
        ...(companyId != null ? { companyId } : {}),
        ...(userId != null ? { userId } : {})
      }
    });
  }

  public getAvailablePaidLicensesByCompanyId({
    hid,
    licenseTypeIds,
    companyId,
    userId
  }: GetAvailablePaidLicensesParams): Observable<Array<AvailablePaidLicense>> {
    return this.http.get<Array<AvailablePaidLicense>>(`${this.baseHref}/authorize/availablelicenses/paid`, {
      params: {
        hid,
        licenseTypeIds,
        offset: 0,
        limit: 1000,
        ...(companyId != null ? { companyId } : {}),
        ...(userId != null ? { userId } : {})
      }
    });
  }

  public getAvailableLicenses(hid: string, licenseTypeId: LicenseType): Observable<AvailableLicenses> {
    return this.http.get<AvailableLicenses>(`${this.baseHref}/availablelicenses?hid=${hid}&licenseTypeId=${licenseTypeId}`);
  }

  public getAvailablePaidLicenses({
    hid,
    licenseTypeIds,
    offset = 0,
    limit = 1000,
    skipError = false
  }: GetAvailablePaidLicensesParams): Observable<Array<AvailablePaidLicense>> {
    const headers: { [key: string]: string } = {};

    if (skipError) {
      headers[MANUAL_ERROR_HANDLED] = MANUAL_ERROR_HANDLED;
    }

    return this.http.get<Array<AvailablePaidLicense>>(`${this.baseHref}/availablelicenses/paid`, {
      params: {
        hid,
        licenseTypeIds,
        offset,
        limit
      },
      headers
    });
  }

  public getTypes(): Observable<LicenseTypeSummary[]> {
    return this.http.get<LicenseTypeSummary[]>('/api/license-types/all');
  }

  public getTypesByOwnerId(): Observable<Array<LicenseType>> {
    return this.http.get<Array<LicenseType>>(`api/license-types/licenses/owner`);
  }

  public getTypesForHistoryByOwnerId(): Observable<Array<LicenseType>> {
    return this.http.get<Array<LicenseType>>(`api/license-types/histories/owner`);
  }

  public getUpcomingRenew(): Observable<Billing> {
    return this.http.get<Billing>('api/licenses/upcoming-renewal');
  }

  public getRenew(params?: PrePaymentParams): Observable<RenewResponse> {
    return this.http.post<RenewResponse>('api/licenses/renew', { params });
  }

  public renew(params?: PrePaymentParams): Observable<string> {
    return this.http.post<string>('api/licenses/renew-link', { params, responseType: 'text', observe: 'response' });
  }

  public tryPaymentAgain() {
    return this.http.get('api/licenses/try-payment-again', { responseType: 'text', observe: 'response' });
  }

  public activate(licenseKey: string) {
    return this.http.put(
      'api/licenses/activate',
      { licenseKey },
      {
        headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
      }
    );
  }

  public getPrePaymentLicenses(params?: PrePaymentParams): Observable<PrePaymentResponse> {
    return this.http.post<PrePaymentResponse>('/api/licenses', params);
  }

  public exportPrePaymentLicenses(params?: PrePaymentParams): Observable<Array<PrePaymentLicense>> {
    return this.http.post<Array<PrePaymentLicense>>('/api/licenses/export', params);
  }

  public getPrePaymentLicensesTotal(params?: PrePaymentParams): Observable<PrePaymentTotals> {
    return this.http.post<PrePaymentTotals>('/api/licenses/totals', params);
  }

  public getPrePaymentLicensesGrouped(params?: PrePaymentParams) {
    return this.http.post<PrePaymentResponseGrouped>('/api/licenses/grouped', params);
  }

  public exportPrePaymentLicensesGrouped(params?: PrePaymentParams): Observable<Array<PrePaymentGroup>> {
    return this.http.post<Array<PrePaymentGroup>>('/api/licenses/grouped/export', params);
  }

  public getLicenses(params?: PostPaymentParams): Observable<ResponseGeneric<Array<LicenseUsageItem>>> {
    return this.http.get<ResponseGeneric<Array<LicenseUsageItem>>>(`${this.baseVersionedHref}/usage`, {
      params: objectToParams(params || {})
    });
  }

  public tryGrantLicense(hid: string): Observable<ComputerGrantLicenseResultDTO> {
    return this.http.post<ComputerGrantLicenseResultDTO>(`${this.baseComputersHref}/${hid}/grantlicense/trygrant`, '');
  }

  public grantLicenseByType(hid: string, type: string) {
    return this.http.post(`${this.baseComputersHref}/${hid}/grantlicense/grantbytype`, { type });
  }

  public getPPLicensesTypes(params?: HttpParams): Observable<ResponseGeneric<Array<LicenseTypeDto>>> {
    return this.http.get<ResponseGeneric<Array<LicenseTypeDto>>>(`${this.baseVersionedHref}/usage/types`, { params });
  }

  public getPPProviderLicensesTypes(): Observable<Array<LicenseTypeDto>> {
    return this.http.get<Array<LicenseTypeDto>>(`${this.baseVersionedHref}/usage/provider-types`);
  }

  public getLicenseTotals(params?: PostPaymentParams): Observable<Total> {
    return this.http.get<Total>(`${this.baseVersionedHref}/usage/totals`, { params: objectToParams(params || {}) });
  }

  public getFreeLicenses(month: string): Observable<FreeLicense[]> {
    return this.http.get<FreeLicense[]>(`${this.baseVersionedHref}/usage/free`, { params: objectToParams({ month }) });
  }

  public getNextPaymentData(month: string): Observable<NextPaymentData> {
    return this.http.get<NextPaymentData>(`${this.baseVersionedHref}/usage/next-payment`, { params: objectToParams({ month }) });
  }

  public setAutoRenewal(params?: AutoRenewalRequest): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedHref}/usage/set-auto-renewal`, params);
  }

  public setAutoRenewalForPrePayment(payload: PrePaymentAutoRenewPayload): Observable<null> {
    return this.http.put<null>('api/licenses/set-auto-renewal', payload);
  }

  public releaseLicenseForPrePayment(payload: PrePaymentPayload) {
    return this.http.put('api/licenses/release', payload);
  }

  public deleteLicense(payload: PrePaymentPayload) {
    return this.http.delete('api/licenses/delete', { body: payload });
  }

  public setAutoRenewalForComputerLicense(hid: string, licenseTypeId: number | string, enabled: boolean): Observable<null> {
    return this.http.put<null>(`${this.baseHref}/computer/set-auto-renewal`, {
      hid,
      licenseTypeId,
      enabled
    });
  }

  public licenseRelease(params?: ReleaseRequest): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedHref}/usage/release`, params);
  }

  public licenseManage(params?: ManageRequest): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedHref}/usage/manage`, params);
  }

  public licenseAssign(params?: AssignRequest): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedHref}/usage/assign`, params);
  }

  public licenseExport(params?: PostPaymentParams) {
    return this.http.get<Array<LicenseUsageItem>>(`${this.baseVersionedHref}/usage/export`, { params: objectToParams(params || {}) });
  }

  public calculateReleaseDate(id: string | number): Observable<{ releaseDate: string }> {
    return this.http.get<{ releaseDate: string }>(
      `${this.baseVersionedHref}/usage/calculate-release-date?licenseUsageId=${id}&setAutoRenewalEnable=false`
    );
  }

  public licenseHideTrial(id: string | number): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedHref}/usage/${id}/set-hidden`, null);
  }

  public getLicenseStatistic(companies: string[]): Observable<LicenseStatistic> {
    const params: { companyIds?: string[] } = {};

    if (companies?.length) params.companyIds = companies;

    return this.http.get<LicenseStatistic>(`${this.baseHref}/statistic`, { params });
  }

  public getBalance(): Observable<Balance> {
    return this.http.get<Balance>(`${this.baseSubscriptionHref}/balance`);
  }

  public getSubscriptionEditLink(): Observable<string> {
    return this.http.get<string>(`${this.baseSubscriptionHref}/edit-link`, { headers: { 'x-front-queue': 'true' } });
  }

  public getDebt(): Observable<Debt> {
    return this.http.get<Debt>(`${this.baseSubscriptionHref}/debt`);
  }

  public getPayDebt(): Observable<PayDebt> {
    return this.http.put<PayDebt>(`${this.baseSubscriptionHref}/pay-debt`, null);
  }

  public getUnpaidInvoicesDetails(): Observable<DebtInvoicesResponse> {
    return this.http.get<DebtInvoicesResponse>(`${this.baseSubscriptionHref}/unpaid-invoices-details`);
  }

  public requestToSwitchToAnotherMode(toAnnual: boolean): Observable<null> {
    return this.http.put<null>(`${this.baseSubscriptionHref}/switch`, { toAnnual, toMonthly: !toAnnual });
  }

  public getPool(groupType: number, groupValue: string): Observable<Array<LicensePool>> {
    return this.http.get<Array<LicensePool>>(`${this.baseVersionedLimitsHref}/${groupType}/${groupValue}`);
  }

  public setPool(limits: Array<LicensePoolLimit>): Observable<Array<LicensePool> & { errors: { [key: string]: number } }> {
    return this.http.post<Array<LicensePool> & { errors: { [key: string]: number } }>(this.baseVersionedLimitsHref, limits);
  }

  public removePool(limits: Array<LicensePoolLimit>): Observable<null> {
    return this.http.post<null>(`${this.baseVersionedLimitsHref}/remove`, limits);
  }

  public releaseComputerLicense(hid: string, licenseTypeId: string | number): Observable<null> {
    return this.http.put<null>(`${this.baseHref}/computer/release`, { hid, licenseTypeId });
  }

  public assignComputerLicense(params: SelectLicenseParams): Observable<ComputerLicense> {
    return this.http.post<ComputerLicense>(`${this.baseHref}/computer/assign`, { ...params });
  }

  public getSupportedLicenseTypes(hid: string, kind: number, companyId?: string, userId?: string): Observable<Array<AvailableLicenseType>> {
    return this.http.get<Array<AvailableLicenseType>>('api/license-types/computer', {
      params: {
        hid,
        kind,
        ...(companyId != null ? { companyId } : {}),
        ...(userId != null ? { userId } : {})
      }
    });
  }

  public getLicenseUsageHistory(params): Observable<UsageHistoryResponse> {
    return this.http.get<UsageHistoryResponse>('api/license-usage-histories', { params });
  }

  public getGroupedLicenseUsageHistory(params): Observable<UsageHistoryGroupedResponse> {
    return this.http.get<UsageHistoryGroupedResponse>('api/license-usage-histories/grouped', { params });
  }

  public exportLicenseUsageHistory(params): Observable<any> {
    return this.http.get<any>('api/license-usage-histories/export', { params });
  }

  public exportGroupedLicenseUsageHistory(params): Observable<any> {
    return this.http.get<any>('api/license-usage-histories/grouped/export', { params });
  }

  public syncLicenses(): Observable<null> {
    return this.http.get<null>('api/licenses/aws/sync');
  }

  public bulkGrant(params: PrePaymentParams | { ids: Array<string> }): Observable<null> {
    return this.http.post<null>('api/licenses/grant', params, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public grant(id: string): Observable<null> {
    return this.http.post<null>(`api/licenses/grant/${id}`, null, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public getPermissionFlags(): Observable<PermissionFlags> {
    return this.http.get<PermissionFlags>('api/licenses/permission-flags');
  }

  public assignLicense(administratorId: string, licenseType: LicenseType, isTrial: boolean): Observable<AdministratorLicense> {
    return this.http.post<AdministratorLicense>(`${this.baseAdministratorsHref}/${administratorId}/license`, { licenseType, isTrial });
  }

  public getAvailableLicenseSummaries(): Observable<AdministratorLicenseSummaryInfo[]> {
    return this.http.get<AdministratorLicenseSummaryInfo[]>(`${this.baseHref}/summary/available_license`, {
      params: { licenseType: LicenseType.RemoteDesktop + ',' + LicenseType.RMM }
    });
  }

  public removeLicense(administratorId: string): Observable<any> {
    return this.http.delete(`${this.baseAdministratorsHref}/${administratorId}/license`);
  }

  public getPrices() {
    return this.http.get<Omit<LicensesPricesData, 'licenseTypesPricesMap'>>('api/calculator/content');
  }

  public getTotals(payload: GetTotalPayload) {
    return this.http.post<TotalResponse>('api/calculator/total', payload);
  }

  public getPurchase(payload: GetPurchasePayload) {
    return this.http.post<{ link: string; needRedirect: boolean }>('api/calculator/purchase', payload);
  }

  public assignBulkLicenses(params: {
    administratorIds?: string[];
    licenseType: LicenseType;
    isTrial: boolean;
    search?: string;
  }): Observable<null> {
    return this.http.post<null>(`${this.baseAdministratorsHref}/licenses`, params);
  }

  public getLicensesGroupedByCompanies(params: GroupByCompanyParams): Observable<GroupByCompanyResponse> {
    return this.http.post<GroupByCompanyResponse>(`${this.baseHref}/grouped/by-companies`, params);
  }

  public getLicensesGroupedByCompaniesForExport(params: GroupByCompanyParams): Observable<any> {
    return this.http.post(`${this.baseHref}/grouped/by-companies/export`, params, { responseType: 'text' });
  }

  public assignTrialRestoreToCloudLicense(): Observable<null> {
    return this.http.post<null>(`${this.baseProviderHref}/assign-trial-restore-to-cloud`, null);
  }
}
